import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Checkbox,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Groups2RoundedIcon from '@mui/icons-material/Groups2Rounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import WifiRoundedIcon from '@mui/icons-material/WifiRounded';
import { API_BASE_URL, tryPaths, fetchJson } from './api';
import { AnalyticsInlineState, AnalyticsMetricCard, AnalyticsPageHero, AnalyticsPanelSection, glassSubCardSx } from './analyticsSurface';

const TEAM_KEY = 'monsurv_team';

function loadTeam() {
    try {
        const raw = localStorage.getItem(TEAM_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch (e) {
        return [];
    }
}

function saveTeam(list) {
    try { localStorage.setItem(TEAM_KEY, JSON.stringify(list)); } catch (e) { }
}

const TEAM_API_PATHS = ['/team', '/api/team', '/team_members', '/api/team_members'];
const TEAM_PRESENCE_API_PATHS = ['/team/presence', '/api/team/presence'];

const ROLES = [
    { key: 'admin', label: 'Admin' },
    { key: 'finance', label: 'Finance' },
    { key: 'funder', label: 'Funder' },
    { key: 'developer', label: 'Developer' },
    { key: 'surveyor', label: 'Surveyor' },
    { key: 'demo', label: 'Demo' },
    { key: 'standard', label: 'Standard' },
];

// For now, the Teams "Add Team Member" modal only supports creating these account types.
// Other account types (e.g. Funder/Developer) are provisioned elsewhere.
// Demo will be expanded in a future update.
const ADD_MEMBER_ROLE_CHOICES = [
    { key: 'admin', label: 'Admin' },
    { key: 'surveyor', label: 'Surveyor' },
    { key: 'finance', label: 'Finance' },
    { key: 'demo', label: 'Demo' },
];

function generateTempPassword(length = 12) {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    let out = '';
    for (let i = 0; i < length; i++) {
        out += chars[Math.floor(Math.random() * chars.length)];
    }
    return out;
}

function getInitials(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function clampPct(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.max(0, Math.min(100, Math.round(x)));
}

function pctColor(pct) {
    if (pct >= 90) return '#b91c1c';
    if (pct >= 75) return '#b45309';
    return '#047857';
}

function ProgressBar({ pct }) {
    const p = clampPct(pct);
    const fg = pctColor(p);
    return (
        <Box sx={{ width: 160 }}>
            <Box
                sx={{
                    height: 10,
                    backgroundColor: alpha('#cbd5e1', 0.7),
                    borderRadius: 999,
                    overflow: 'hidden'
                }}
            >
                <Box sx={{ height: 10, width: `${p}%`, background: fg }} />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block', fontWeight: 700 }}>
                {p}%
            </Typography>
        </Box>
    );
}

function getAuthHeader() {
    try {
        const token = localStorage.getItem('monsurv_auth_token') || '';
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

function normalizeMember(m) {
    if (!m || typeof m !== 'object') return null;
    const role = String(m.role ?? m.access_role ?? '').trim().toLowerCase() || 'standard';
    const fallbackId = m.email || m.name || `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    return {
        id: m.id ?? m.member_id ?? m.team_member_id ?? fallbackId,
        name: m.name ?? m.full_name ?? '',
        position: m.position ?? '',
        email: m.email ?? '',
        phone: m.phone ?? '',
        role: ['admin', 'finance', 'funder', 'developer', 'surveyor', 'demo', 'standard'].includes(role) ? role : 'standard',
        profile_photo_url: m.profile_photo_url ?? m.profilePhotoUrl ?? m.profile_photo ?? null,
    };
}

async function fetchTeamFromApi() {
    const data = await tryPaths(
        TEAM_API_PATHS,
        (path) => `${API_BASE_URL}${path}`,
        { method: 'GET', headers: { ...getAuthHeader() } }
    );
    const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
    return list.map(normalizeMember).filter(Boolean);
}

async function fetchTeamPresenceFromApi() {
    const data = await tryPaths(
        TEAM_PRESENCE_API_PATHS,
        (path) => `${API_BASE_URL}${path}`,
        { method: 'GET', headers: { ...getAuthHeader() } }
    );

    const members = Array.isArray(data?.members) ? data.members : [];
    const map = {};
    for (const row of members) {
        const id = row?.team_member_id;
        if (id === null || id === undefined || id === '') continue;
        map[String(id)] = {
            online: Boolean(row?.online),
            status: String(row?.status || '').trim().toLowerCase() || null
        };
    }
    return map;
}

async function createMemberOnApi(payload) {
    return tryPaths(
        TEAM_API_PATHS,
        (path) => `${API_BASE_URL}${path}`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify(payload)
        }
    );
}

async function updateMemberOnApi(id, payload) {
    return tryPaths(
        TEAM_API_PATHS,
        (path) => `${API_BASE_URL}${path}/${encodeURIComponent(id)}`,
        {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify(payload)
        }
    );
}

async function deleteMemberOnApi(id, deletionPassword) {
    return tryPaths(
        TEAM_API_PATHS,
        (path) => `${API_BASE_URL}${path}/${encodeURIComponent(id)}`,
        {
            method: 'DELETE',
            headers: {
                ...getAuthHeader(),
                ...(deletionPassword ? { 'X-Deletion-Password': String(deletionPassword) } : {})
            }
        }
    );
}

export default function Team({ authUser }) {
    const [team, setTeam] = useState(() => loadTeam());
    const [form, setForm] = useState({ name: '', position: '', email: '', phone: '', role: 'surveyor', createAccount: false, tempPassword: '' });
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', position: '', email: '', phone: '', role: 'standard', createAccount: false, tempPassword: '' });
    const [showTeamForm, setShowTeamForm] = useState(false);
    const [actionMessage, setActionMessage] = useState('');
    const [expandedIds, setExpandedIds] = useState([]);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuMemberId, setMenuMemberId] = useState(null);
    const [workloadRows, setWorkloadRows] = useState([]);
    const [workloadLoading, setWorkloadLoading] = useState(false);
    const [workloadError, setWorkloadError] = useState('');
    const [analyticsOverview, setAnalyticsOverview] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [analyticsError, setAnalyticsError] = useState('');

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [deleteTargetLabel, setDeleteTargetLabel] = useState('');
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const [presenceByMemberId, setPresenceByMemberId] = useState({});

    const isHydratedFromApi = useRef(false);

    const authRole = String(authUser?.role || '').toLowerCase();
    const isAdmin = authRole === 'admin';
    const canSeePresence = Boolean(authUser);

    useEffect(() => {
        if (!canSeePresence) return;
        let cancelled = false;
        let inFlight = false;

        // When you're on the Teams page, you are by definition "online".
        // Optimistically mark the current user online immediately so the UI doesn't lag behind the next poll.
        try {
            const myUsername = String(authUser?.username || '').trim().toLowerCase();
            const myEmail = String(authUser?.email || '').trim().toLowerCase();
            const myTeamMember = (team || []).find((m) => {
                const e = String(m?.email || '').trim().toLowerCase();
                if (!e) return false;
                return (myEmail && e === myEmail) || (myUsername && e === myUsername);
            });
            const myId = myTeamMember?.id;
            if (myId !== null && myId !== undefined && myId !== '') {
                setPresenceByMemberId((prev) => {
                    const key = String(myId);
                    const existing = prev?.[key] || null;
                    if (existing && (existing.status === 'online' || existing.online === true)) return prev;
                    return { ...(prev || {}), [key]: { online: true, status: 'online' } };
                });
            }
        } catch {
            // ignore
        }

        const tick = async () => {
            if (inFlight) return;
            inFlight = true;
            try {
                const map = await fetchTeamPresenceFromApi();
                if (!cancelled) setPresenceByMemberId(map || {});
            } catch {
                // Ignore: presence is best-effort (e.g. backend not available or not authorized).
            } finally {
                inFlight = false;
            }
        };

        tick();
        const timer = setInterval(tick, 5_000);

        const onFocus = () => { void tick(); };
        const onVisibility = () => {
            try {
                if (document.visibilityState === 'visible') void tick();
            } catch {
                // ignore
            }
        };

        try {
            window.addEventListener('focus', onFocus);
            document.addEventListener('visibilitychange', onVisibility);
        } catch {
            // ignore
        }
        return () => {
            cancelled = true;
            clearInterval(timer);
            try {
                window.removeEventListener('focus', onFocus);
                document.removeEventListener('visibilitychange', onVisibility);
            } catch {
                // ignore
            }
        };
    }, [canSeePresence, authUser, team]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const list = await fetchTeamFromApi();
                if (!cancelled) {
                    isHydratedFromApi.current = true;
                    setTeam(list);
                }
            } catch (e) {
                // Backend not available yet; keep localStorage fallback.
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setAnalyticsLoading(true);
            setAnalyticsError('');
            try {
                const data = await fetchJson(`${API_BASE_URL}/api/team/analytics/overview?horizon_days=30`, {
                    method: 'GET',
                    headers: { ...getAuthHeader() }
                });
                if (!cancelled) setAnalyticsOverview(data && typeof data === 'object' ? data : null);
            } catch (e) {
                if (!cancelled) {
                    setAnalyticsOverview(null);
                    setAnalyticsError(String(e?.message || 'Failed to load team analytics overview'));
                }
            } finally {
                if (!cancelled) setAnalyticsLoading(false);
            }

            setWorkloadLoading(true);
            setWorkloadError('');
            try {
                const data = await fetchJson(`${API_BASE_URL}/api/workload/overview?horizon_days=30`, {
                    method: 'GET',
                    headers: { ...getAuthHeader() }
                });
                const list = Array.isArray(data?.surveyors) ? data.surveyors : [];
                if (!cancelled) setWorkloadRows(list);
            } catch (e) {
                if (!cancelled) {
                    setWorkloadRows([]);
                    setWorkloadError(String(e?.message || 'Failed to load workload'));
                }
            } finally {
                if (!cancelled) setWorkloadLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const workloadByName = useMemo(() => {
        const map = new Map();
        (workloadRows || []).forEach((row) => {
            const key = String(row?.name || '').trim().toLowerCase();
            if (key) map.set(key, row);
        });
        return map;
    }, [workloadRows]);

    const teamMetrics = useMemo(() => {
        const apiTotals = analyticsOverview?.totals && typeof analyticsOverview.totals === 'object'
            ? analyticsOverview.totals
            : null;
        if (apiTotals) {
            return {
                total: Number(apiTotals.team_members || 0),
                onlineCount: Number(apiTotals.online_now || 0),
                surveyorCount: Number(apiTotals.surveyors || 0),
                pressuredCount: Number(apiTotals.pressure_watch || 0),
            };
        }

        const list = Array.isArray(team) ? team : [];
        const presence = presenceByMemberId && typeof presenceByMemberId === 'object' ? presenceByMemberId : {};
        const onlineCount = list.filter((member) => {
            const row = presence[String(member?.id)] || null;
            return String(row?.status || '').toLowerCase() === 'online' || row?.online === true;
        }).length;
        const surveyorCount = list.filter((member) => String(member?.role || '').toLowerCase() === 'surveyor').length;
        const pressuredCount = (workloadRows || []).filter((row) => Number(row?.utilisation_pct || 0) >= 75).length;
        return {
            total: list.length,
            onlineCount,
            surveyorCount,
            pressuredCount,
        };
    }, [analyticsOverview, presenceByMemberId, team, workloadRows]);

    const roleDistribution = useMemo(() => {
        const fromApi = Array.isArray(analyticsOverview?.role_distribution) ? analyticsOverview.role_distribution : [];
        if (fromApi.length) {
            return fromApi
                .map((row) => ({
                    role: String(row?.role || 'standard').trim().toLowerCase() || 'standard',
                    count: Number(row?.count || 0)
                }))
                .filter((row) => row.count > 0)
                .sort((a, b) => b.count - a.count || a.role.localeCompare(b.role));
        }

        const counts = new Map();
        for (const member of (Array.isArray(team) ? team : [])) {
            const role = String(member?.role || 'standard').trim().toLowerCase() || 'standard';
            counts.set(role, Number(counts.get(role) || 0) + 1);
        }
        return Array.from(counts.entries())
            .map(([role, count]) => ({ role, count }))
            .sort((a, b) => b.count - a.count || a.role.localeCompare(b.role));
    }, [analyticsOverview, team]);

    const pressureWatchRows = useMemo(() => {
        const fromApi = Array.isArray(analyticsOverview?.pressure_watch_rows) ? analyticsOverview.pressure_watch_rows : [];
        if (fromApi.length) {
            return fromApi.slice(0, 5).map((row) => ({
                name: String(row?.name || '').trim() || 'Unknown',
                utilisation_pct: Number(row?.utilisation_pct || 0),
                active_projects: Number(row?.active_projects || 0)
            }));
        }
        return (Array.isArray(workloadRows) ? workloadRows : [])
            .filter((row) => Number(row?.utilisation_pct || 0) >= 75)
            .sort((a, b) => Number(b?.utilisation_pct || 0) - Number(a?.utilisation_pct || 0))
            .slice(0, 5)
            .map((row) => ({
                name: String(row?.name || '').trim() || 'Unknown',
                utilisation_pct: Number(row?.utilisation_pct || 0),
                active_projects: Number(row?.active_projects || 0)
            }));
    }, [analyticsOverview, workloadRows]);

    useEffect(() => {
        // Keep localStorage as an offline fallback cache.
        saveTeam(team);
        try { window.dispatchEvent(new CustomEvent('monsurv:team-updated', { detail: team })); } catch (e) { }
    }, [team]);

    function handleChange(e) {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
    }

    async function addMember(e) {
        e.preventDefault();
        if (!isAdmin) return;
        if (!form.name || !form.position) return;
        if (!form.role) return;

        const allowedAddRoles = ADD_MEMBER_ROLE_CHOICES.map((r) => r.key);
        const role = allowedAddRoles.includes(String(form.role).toLowerCase()) ? String(form.role).toLowerCase() : 'surveyor';

        setActionMessage('');

        const payload = {
            name: form.name,
            position: form.position,
            email: form.email,
            phone: form.phone,
            role,
        };

        if (form.createAccount) {
            payload.createAccount = true;
            payload.tempPassword = form.tempPassword;
        }

        if (isHydratedFromApi.current) {
            try {
                const created = await createMemberOnApi(payload);
                const normalized = normalizeMember(created?.data || created) || { id: 'm-' + Date.now().toString(36), ...payload };
                setTeam(list => [normalized, ...list]);
                if (payload.createAccount) {
                    const sent = Boolean(created?.inviteEmailSent || created?.data?.inviteEmailSent);
                    setActionMessage(sent ? 'Account created and invite email sent.' : 'Account created (email not sent - check SMTP config/logs).');
                }
            } catch (e) {
                const member = { id: 'm-' + Date.now().toString(36), ...payload };
                setTeam(list => [member, ...list]);
                if (payload.createAccount) setActionMessage('Team member saved, but account creation may have failed (check backend logs).');
            }
        } else {
            const member = { id: 'm-' + Date.now().toString(36), ...payload };
            setTeam(list => [member, ...list]);
        }
        setForm({ name: '', position: '', email: '', phone: '', role: 'surveyor', createAccount: false, tempPassword: '' });
        setShowTeamForm(false);
    }

    async function removeMember(id) {
        if (!isAdmin) return;

        const member = team.find((m) => String(m?.id) === String(id)) || null;
        const label = member?.name ? `"${member.name}"` : 'this team member';

        setDeleteTargetId(id);
        setDeleteTargetLabel(label);
        setDeletePassword('');
        setDeleteError('');
        setDeleteDialogOpen(true);
    }

    function closeDeleteDialog() {
        if (deleteBusy) return;
        setDeleteDialogOpen(false);
        setDeleteTargetId(null);
        setDeleteTargetLabel('');
        setDeletePassword('');
        setDeleteError('');
    }

    async function confirmDeleteMember() {
        if (!isAdmin) return;
        const id = deleteTargetId;
        const pw = String(deletePassword || '').trim();
        if (id === null || id === undefined || id === '') return;
        if (!pw) {
            setDeleteError('Deletion password is required.');
            return;
        }

        setDeleteBusy(true);
        setDeleteError('');
        try {
            if (isHydratedFromApi.current) {
                await deleteMemberOnApi(id, pw);
            }

            setTeam(list => list.filter(m => m.id !== id));
            if (editingId === id) cancelEdit();
            setDeleteDialogOpen(false);
            setDeleteTargetId(null);
            setDeleteTargetLabel('');
            setDeletePassword('');
            setDeleteError('');
        } catch (e) {
            setDeleteError(String(e?.message || 'Failed to delete team member'));
        } finally {
            setDeleteBusy(false);
        }
    }

    function startEdit(member) {
        if (!isAdmin) return;
        setEditingId(member.id);
        setEditForm({
            name: member.name,
            position: member.position,
            email: member.email || '',
            phone: member.phone || '',
            role: member.role || 'standard',
            createAccount: false,
            tempPassword: '',
        });
    }

    function cancelEdit() {
        setEditingId(null);
        setEditForm({ name: '', position: '', email: '', phone: '', role: 'standard', createAccount: false, tempPassword: '' });
    }

    function toggleExpanded(id) {
        setExpandedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    }

    function openCardMenu(e, memberId) {
        e.preventDefault();
        e.stopPropagation();
        setMenuAnchorEl(e.currentTarget);
        setMenuMemberId(String(memberId));
    }

    function emailMember(member) {
        const email = String(member?.email || '').trim();
        if (!email) return;
        const subject = encodeURIComponent('Monsurv Team');
        window.location.href = `mailto:${email}?subject=${subject}`;
    }

    function closeCardMenu() {
        setMenuAnchorEl(null);
        setMenuMemberId(null);
    }

    async function saveEdit(e) {
        e.preventDefault();
        if (!isAdmin) return;
        if (!editForm.name || !editForm.position) return;
        if (!editForm.role) return;

        setActionMessage('');

        const payload = {
            name: editForm.name,
            position: editForm.position,
            email: editForm.email,
            phone: editForm.phone,
            role: editForm.role,
        };

        if (editForm.createAccount) {
            payload.createAccount = true;
            payload.tempPassword = editForm.tempPassword;
        }

        if (isHydratedFromApi.current) {
            try {
                const updated = await updateMemberOnApi(editingId, payload);
                if (payload.createAccount) {
                    const sent = Boolean(updated?.inviteEmailSent || updated?.data?.inviteEmailSent);
                    setActionMessage(sent ? 'Account created and invite email sent.' : 'Account created (email not sent - check SMTP config/logs).');
                }
            } catch (e) {
                if (payload.createAccount) setActionMessage('Saved changes, but account creation may have failed (check backend logs).');
            }
        }

        setTeam(list => list.map(m => m.id === editingId ? { ...m, ...payload } : m));
        cancelEdit();
    }

    return (
        <Box className="team" sx={{ position: 'relative', display: 'grid', gap: 2.25 }}>
            {actionMessage ? (
                <Paper sx={{ p: 1.5, bgcolor: (t) => alpha(t.palette.info.light, 0.18), borderColor: 'info.light', borderRadius: 3 }}>
                    <Typography variant="body2" sx={{ color: 'info.dark' }}>{actionMessage}</Typography>
                </Paper>
            ) : null}
            <AnalyticsPageHero
                eyebrow="Delivery Team"
                title="People, presence, and workload"
                actions={
                    isAdmin ? (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                setForm({ name: '', position: '', email: '', phone: '', role: 'surveyor', createAccount: false, tempPassword: '' });
                                setShowTeamForm(true);
                            }}
                        >
                            Add member
                        </Button>
                    ) : null
                }
            />

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                    gap: 2,
                }}
            >
                <AnalyticsMetricCard
                    icon={<Groups2RoundedIcon fontSize="small" />}
                    label="Team Size"
                    value={teamMetrics.total}
                    caption="All active members currently available on the team roster."
                    accent="linear-gradient(135deg, #2563eb, #0f172a)"
                />
                <AnalyticsMetricCard
                    icon={<WifiRoundedIcon fontSize="small" />}
                    label="Online Now"
                    value={teamMetrics.onlineCount}
                    caption="Best-effort live presence from the existing team presence polling."
                    accent="linear-gradient(135deg, #0f766e, #14b8a6)"
                />
                <AnalyticsMetricCard
                    icon={<PersonSearchRoundedIcon fontSize="small" />}
                    label="Surveyors"
                    value={teamMetrics.surveyorCount}
                    caption="Operational delivery roles currently assigned as surveyors."
                    accent="linear-gradient(135deg, #7c3aed, #2563eb)"
                />
                <AnalyticsMetricCard
                    icon={<AssignmentTurnedInRoundedIcon fontSize="small" />}
                    label="Pressure Watch"
                    value={teamMetrics.pressuredCount}
                    caption="Members with workload utilisation at or above the current alert threshold."
                    accent="linear-gradient(135deg, #b45309, #ef4444)"
                />
            </Box>

            <AnalyticsPanelSection
                eyebrow="Team Analytics"
                title="Role mix and pressure distribution"
                right={
                    <Chip
                        size="small"
                        label={analyticsLoading ? 'Refreshing...' : (analyticsError ? 'Aggregate unavailable' : 'Aggregate live')}
                        sx={{ fontWeight: 800, backgroundColor: alpha('#eef4fb', 0.9) }}
                    />
                }
            >
                {analyticsError ? <Alert severity="info" sx={{ mb: 1.2 }}>{analyticsError}</Alert> : null}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.1fr 1fr' }, gap: 1.5 }}>
                    <Paper sx={{ ...glassSubCardSx, p: 1.3 }}>
                        <Typography sx={{ fontWeight: 800, mb: 1 }}>Role distribution</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {roleDistribution.map((item) => (
                                <Chip
                                    key={item.role}
                                    label={`${String(item.role).replace(/_/g, ' ')}: ${item.count}`}
                                    sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                                />
                            ))}
                            {!roleDistribution.length ? <Chip label="No role data" /> : null}
                        </Stack>
                    </Paper>

                    <Paper sx={{ ...glassSubCardSx, p: 1.3 }}>
                        <Typography sx={{ fontWeight: 800, mb: 1 }}>Pressure watchlist</Typography>
                        <Stack spacing={0.7}>
                            {pressureWatchRows.map((row) => (
                                <Stack key={`${row.name}-${row.utilisation_pct}`} direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.name}</Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {Math.round(row.utilisation_pct)}% util. · {row.active_projects} projects
                                    </Typography>
                                </Stack>
                            ))}
                            {!pressureWatchRows.length ? (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>No members currently above the pressure threshold.</Typography>
                            ) : null}
                        </Stack>
                    </Paper>
                </Box>
            </AnalyticsPanelSection>

            {/* Modal for Add Team Member Form */}
            {showTeamForm && (
                <Dialog
                    open
                    onClose={() => {
                        setForm({ name: '', position: '', email: '', phone: '', role: 'surveyor', createAccount: false, tempPassword: '' });
                        setShowTeamForm(false);
                    }}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Add Team Member</DialogTitle>
                    <DialogContent dividers>
                        <Stack id="team-add-form" component="form" onSubmit={addMember} spacing={2} sx={{ mt: 1 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField fullWidth label="Name" name="name" value={form.name} onChange={handleChange} />
                                <TextField fullWidth label="Position" name="position" value={form.position} onChange={handleChange} />
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField fullWidth label="Email" name="email" value={form.email} onChange={handleChange} />
                                <TextField fullWidth label="Phone" name="phone" value={form.phone} onChange={handleChange} />
                            </Stack>
                            <Box>
                                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>Role</Typography>
                                <RolePicker
                                    value={form.role}
                                    onChange={(next) => setForm(f => ({ ...f, role: next }))}
                                    roles={ADD_MEMBER_ROLE_CHOICES}
                                />
                            </Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={Boolean(form.createAccount)}
                                        onChange={(e) => {
                                            const checked = Boolean(e.target.checked);
                                            setForm((f) => ({
                                                ...f,
                                                createAccount: checked,
                                                tempPassword: checked ? (f.tempPassword || generateTempPassword()) : ''
                                            }));
                                        }}
                                    />
                                }
                                label="Create dashboard account (sends email)"
                            />
                            {form.createAccount ? (
                                <Stack spacing={1}>
                                    <TextField
                                        label="Temporary Password"
                                        name="tempPassword"
                                        value={form.tempPassword}
                                        onChange={(e) => setForm((f) => ({ ...f, tempPassword: e.target.value }))}
                                    />
                                    <Box>
                                        <Button type="button" variant="outlined" onClick={() => setForm((f) => ({ ...f, tempPassword: generateTempPassword() }))}>
                                            Generate
                                        </Button>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        The user will be asked to change this on first login.
                                    </Typography>
                                </Stack>
                            ) : null}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setForm({ name: '', position: '', email: '', phone: '', role: 'surveyor', createAccount: false, tempPassword: '' });
                                setShowTeamForm(false);
                            }}
                            variant="text"
                        >
                            Cancel
                        </Button>
                        <Button type="submit" form="team-add-form" variant="contained">Add Member</Button>
                    </DialogActions>
                </Dialog>
            )}

            <AnalyticsPanelSection
                eyebrow="Team Directory"
                title="Member cards"
                right={
                    <Paper sx={{ ...glassSubCardSx, px: 1.4, py: 1 }}>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            Workload status
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 800 }}>
                            {workloadLoading ? 'Loading…' : (workloadError ? 'Unavailable' : `${workloadRows.length} workload rows`)}
                        </Typography>
                    </Paper>
                }
            >
                <Box className="team-list" sx={{ flex: 1 }}>
                    {team.length === 0 ? (
                        <AnalyticsInlineState
                            kind="empty"
                            title="No team members yet"
                            message={isAdmin ? 'Add your first team member to start assigning work and tracking capacity.' : 'No team members are currently available in the roster.'}
                        />
                    ) : (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                                alignItems: 'start',
                                gap: 2,
                            }}
                        >
                            {team.map((m, idx) => {
                                const resolvedId =
                                    String(m.id ?? '').trim() ||
                                    String(m.email || '').trim() ||
                                    String(m.name || '').trim() ||
                                    `m-${idx}`;
                                const expanded = expandedIds.includes(resolvedId);
                                const workload = workloadByName.get(String(m.name || '').trim().toLowerCase()) || null;
                                const photoSrc = m.profile_photo_url ? `${API_BASE_URL}${m.profile_photo_url}` : '';
                                const presence = canSeePresence ? (presenceByMemberId?.[String(m.id)] || null) : null;
                                const status = (String(presence?.status || '').trim().toLowerCase() || 'offline');
                                const dotColor =
                                    status === 'busy' ? 'error.main'
                                        : (status === 'away' ? 'warning.main'
                                            : (status === 'online' ? 'success.main' : 'grey.400'));
                                return (
                                    <Paper
                                        key={resolvedId}
                                        sx={{
                                            p: 2,
                                            borderRadius: 4,
                                            position: 'relative',
                                            border: `1px solid ${alpha('#ffffff', 0.72)}`,
                                            background: `linear-gradient(180deg, ${alpha('#ffffff', 0.92)} 0%, ${alpha('#f7fbff', 0.76)} 100%)`,
                                            boxShadow: '0 18px 42px rgba(15, 23, 42, 0.08)'
                                        }}
                                    >
                                        {editingId === m.id ? (
                                            <Stack component="form" onSubmit={saveEdit} spacing={2}>
                                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                                    <TextField fullWidth label="Name" name="name" value={editForm.name} onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))} />
                                                    <TextField fullWidth label="Position" name="position" value={editForm.position} onChange={(e) => setEditForm(f => ({ ...f, position: e.target.value }))} />
                                                </Stack>
                                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                                    <TextField fullWidth label="Email" name="email" value={editForm.email} onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))} />
                                                    <TextField fullWidth label="Phone" name="phone" value={editForm.phone} onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))} />
                                                </Stack>
                                                <Box>
                                                    <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>Role</Typography>
                                                    <RolePicker
                                                        value={editForm.role}
                                                        onChange={(next) => setEditForm(f => ({ ...f, role: next }))}
                                                    />
                                                </Box>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={Boolean(editForm.createAccount)}
                                                            onChange={(e) => {
                                                                const checked = Boolean(e.target.checked);
                                                                setEditForm((f) => ({
                                                                    ...f,
                                                                    createAccount: checked,
                                                                    tempPassword: checked ? (f.tempPassword || generateTempPassword()) : ''
                                                                }));
                                                            }}
                                                        />
                                                    }
                                                    label="Create/reset dashboard account (email temp password)"
                                                />
                                                {editForm.createAccount ? (
                                                    <Stack spacing={1}>
                                                        <TextField
                                                            label="Temporary Password"
                                                            name="tempPassword"
                                                            value={editForm.tempPassword}
                                                            onChange={(e) => setEditForm((f) => ({ ...f, tempPassword: e.target.value }))}
                                                        />
                                                        <Box>
                                                            <Button type="button" variant="outlined" onClick={() => setEditForm((f) => ({ ...f, tempPassword: generateTempPassword() }))}>
                                                                Generate
                                                            </Button>
                                                        </Box>
                                                    </Stack>
                                                ) : null}
                                                <Stack direction="row" spacing={1}>
                                                    <Button type="submit" variant="contained">Save</Button>
                                                    <Button type="button" variant="text" onClick={cancelEdit}>Cancel</Button>
                                                </Stack>
                                            </Stack>
                                        ) : (
                                            <>
                                                {isAdmin ? (
                                                    <>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => openCardMenu(e, m.id)}
                                                            aria-label="Open actions"
                                                            aria-controls={menuMemberId === String(m.id) && menuAnchorEl ? 'team-card-actions' : undefined}
                                                            aria-haspopup="true"
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 10,
                                                                right: 14,
                                                                background: '#0b1f3a !important',
                                                                backgroundColor: '#0b1f3a !important',
                                                                color: '#fff !important',
                                                                '&:hover': {
                                                                    background: '#08192e !important',
                                                                    backgroundColor: '#08192e !important'
                                                                }
                                                            }}
                                                        >
                                                            <MoreVertIcon fontSize="small" />
                                                        </IconButton>
                                                        <Menu
                                                            id="team-card-actions"
                                                            anchorEl={menuAnchorEl}
                                                            open={Boolean(menuAnchorEl) && menuMemberId === String(m.id)}
                                                            onClose={closeCardMenu}
                                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                        >
                                                            <MenuItem
                                                                onClick={() => {
                                                                    closeCardMenu();
                                                                    startEdit(m);
                                                                }}
                                                            >
                                                                <ListItemIcon>
                                                                    <EditOutlinedIcon fontSize="small" />
                                                                </ListItemIcon>
                                                                Edit
                                                            </MenuItem>
                                                            <MenuItem
                                                                onClick={async () => {
                                                                    closeCardMenu();
                                                                    await removeMember(m.id);
                                                                }}
                                                                sx={{ color: 'error.main' }}
                                                            >
                                                                <ListItemIcon sx={{ color: 'error.main' }}>
                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                </ListItemIcon>
                                                                Remove
                                                            </MenuItem>
                                                        </Menu>
                                                    </>
                                                ) : null}
                                                <Stack direction="row" spacing={2} alignItems={expanded ? 'center' : 'flex-start'}>
                                                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                                        <Avatar
                                                            src={photoSrc || undefined}
                                                            alt={m.name}
                                                            sx={{
                                                                width: expanded ? 98 : 56,
                                                                height: expanded ? 98 : 56,
                                                                bgcolor: 'primary.main',
                                                                transition: 'width 180ms ease, height 180ms ease'
                                                            }}
                                                        >
                                                            {getInitials(m.name)}
                                                        </Avatar>
                                                        {canSeePresence ? (
                                                            <Box
                                                                sx={{
                                                                    position: 'absolute',
                                                                    right: expanded ? 6 : 2,
                                                                    bottom: expanded ? 6 : 2,
                                                                    width: expanded ? 16 : 12,
                                                                    height: expanded ? 16 : 12,
                                                                    borderRadius: '50%',
                                                                    bgcolor: dotColor,
                                                                    border: '2px solid',
                                                                    borderColor: 'background.paper'
                                                                }}
                                                            />
                                                        ) : null}
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0, pr: isAdmin ? 4 : 0, transition: 'margin 180ms ease' }}>
                                                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
                                                            {String(m.role || 'standard')}
                                                        </Typography>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{m.name}</Typography>
                                                        <Typography variant="body2" color="text.secondary">{m.position}</Typography>
                                                        {canSeePresence ? (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ display: 'block', mt: 0.25 }}
                                                            >
                                                                Status: {status}
                                                            </Typography>
                                                        ) : null}
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                            {m.email || ''} {m.phone ? (`• ${m.phone}`) : ''}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => toggleExpanded(resolvedId)}
                                                        aria-label={expanded ? 'Hide details' : 'Show details'}
                                                    >
                                                        {expanded ? 'Hide details' : 'View details'}
                                                    </Button>
                                                    {m.email ? (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<EmailOutlinedIcon fontSize="small" />}
                                                            onClick={() => emailMember(m)}
                                                        >
                                                            Email
                                                        </Button>
                                                    ) : null}
                                                    {isAdmin ? (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<EditOutlinedIcon fontSize="small" />}
                                                            onClick={() => startEdit(m)}
                                                        >
                                                            Edit
                                                        </Button>
                                                    ) : null}
                                                </Stack>
                                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
                                                    <Paper sx={{ ...glassSubCardSx, px: 1.15, py: 0.75 }}>
                                                        <Typography variant="caption" color="text.secondary">Role: {String(m.role || 'standard')}</Typography>
                                                    </Paper>
                                                    {workload ? (
                                                        <Paper sx={{ ...glassSubCardSx, px: 1.15, py: 0.75 }}>
                                                            <Typography variant="caption" color="text.secondary">Utilisation: {clampPct(workload.utilisation_pct)}%</Typography>
                                                        </Paper>
                                                    ) : null}
                                                </Stack>
                                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                                    <IconButton
                                                        onClick={() => toggleExpanded(resolvedId)}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: 'transparent !important',
                                                            color: 'var(--muted)',
                                                            boxShadow: 'none',
                                                            '&:hover': { bgcolor: 'transparent', color: 'var(--muted)' }
                                                        }}
                                                        aria-label={expanded ? 'Hide workload' : 'Show workload'}
                                                        disableRipple
                                                        disableFocusRipple
                                                    >
                                                        <ExpandMoreIcon
                                                            sx={{
                                                                transition: 'transform 160ms ease',
                                                                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                                color: 'var(--muted)',
                                                                fontSize: 26,
                                                                opacity: 1
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Box>
                                                <Collapse in={expanded} timeout="auto" unmountOnExit>
                                                    <Box sx={{ mt: 1, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                                        {workload ? (
                                                            <Stack spacing={1} sx={{ fontSize: 13 }}>
                                                                <Row label="Level" value={workload.role_level || '—'} />
                                                                <Row label="Active projects" value={`${workload.active_project_count ?? 0} / ${workload.max_concurrent_projects ?? '—'}`} />
                                                                <Row label="Utilisation" value={<ProgressBar pct={workload.utilisation_pct} />} align="center" />
                                                                <Row label="Points / capacity" value={`${workload.total_points ?? 0} / ${workload.weekly_capacity_points ?? 0}`} />
                                                                <Row label="Booked slots (14d)" value={workload.booked_slots_14d ?? 0} />
                                                                <Row label="Deadlines (7d)" value={workload.open_deadlines_7d ?? 0} />
                                                                <Row label="RED exposure" value={workload.risk_exposure_red_count ?? 0} />
                                                            </Stack>
                                                        ) : (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {workloadLoading ? 'Loading workload metrics…' : (workloadError || 'No workload metrics available for this member.')}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Collapse>
                                            </>
                                        )}
                                    </Paper>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </AnalyticsPanelSection>

            <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Delete {deleteTargetLabel || 'team member'}</DialogTitle>
                <DialogContent>
                    {deleteError ? <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert> : null}
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This will permanently delete this team member and may also remove their dashboard login. This cannot be undone.
                    </Alert>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Deletion password"
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        disabled={deleteBusy}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} disabled={deleteBusy}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={confirmDeleteMember} disabled={deleteBusy}>
                        {deleteBusy ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

function RolePicker({ value, onChange, roles = ROLES }) {
    const vRaw = String(value || '').toLowerCase() || 'standard';
    const v = roles.some((r) => r.key === vRaw) ? vRaw : (roles[0]?.key || 'standard');
    return (
        <ToggleButtonGroup
            value={v}
            exclusive
            onChange={(_, next) => next && onChange && onChange(next)}
            size="small"
        >
            {roles.map((r) => (
                <ToggleButton key={r.key} value={r.key}>{r.label}</ToggleButton>
            ))}
        </ToggleButtonGroup>
    );
}

function Row({ label, value, align }) {
    return (
        <Stack direction="row" alignItems={align || 'center'} justifyContent="space-between" spacing={2}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Box sx={{ textAlign: 'right' }}>{value}</Box>
        </Stack>
    );
}
